/**
 * Draft mutations for paths (add/remove/move) — applied at the
 * default-paths AND rule-paths levels.
 *
 * Item-level operations (landers / offers) live in `draft-structural-items.ts`
 * to keep each file under the size budget.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  BinomDraftPath,
  BinomFlowDraft,
} from './binom-flow-draft.types';

const NEW_PREFIX = 'new:';

let counter = 0;
function newPathId(): string {
  counter += 1;
  return `${NEW_PREFIX}${Date.now()}-${counter}`;
}

export function blankPath(): BinomDraftPath {
  return {
    id: newPathId(),
    name: 'New path',
    originalName: '',
    weight: 100,
    originalWeight: 100,
    active: true,
    originalActive: true,
    landers: [],
    offers: [],
  };
}

export function addDefaultPath(d: BinomFlowDraft): void {
  d.defaultPaths.push(blankPath());
}

export function removeDefaultPath(d: BinomFlowDraft, idx: number): void {
  if (idx < 0 || idx >= d.defaultPaths.length) return;
  d.defaultPaths.splice(idx, 1);
}

export function moveDefaultPath(d: BinomFlowDraft, from: number, to: number): void {
  moveInPlace(d.defaultPaths, from, to);
}

export function addGroupPath(d: BinomFlowDraft, groupIdx: number): void {
  const g = d.pathGroups[groupIdx];
  if (!g) return;
  g.paths.push(blankPath());
}

export function removeGroupPath(
  d: BinomFlowDraft,
  groupIdx: number,
  pathIdx: number,
): void {
  const g = d.pathGroups[groupIdx];
  if (!g) return;
  if (pathIdx < 0 || pathIdx >= g.paths.length) return;
  g.paths.splice(pathIdx, 1);
}

export function moveGroupPath(
  d: BinomFlowDraft,
  groupIdx: number,
  from: number,
  to: number,
): void {
  const g = d.pathGroups[groupIdx];
  if (!g) return;
  moveInPlace(g.paths, from, to);
}

export {
  addLander,
  addOffer,
  getPath,
  moveLander,
  moveOffer,
  removeLander,
  removeOffer,
  type PathLocator,
} from './draft-structural-items';

function moveInPlace<T>(arr: T[], from: number, to: number): void {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= arr.length ||
    to >= arr.length
  )
    return;
  const [item] = arr.splice(from, 1);
  arr.splice(to, 0, item);
}
